import React, { useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Page from '../../../components/Page';
import AuthContext from '../../../contexts/AuthContext';
import useAPI from '../../../hooks/useAPI';
import { COMPLETED_STATUSES } from '../../../enums/paymentStatuses';
import { formatDate } from './utils';
import DashboardStats from './DashboardStats';
import RecentPayments from './RecentPayments';
import RegistrationProgress from './RegistrationProgress';
import ContractDetails from './ContractDetails';
import QuickActions from './QuickActions';
import './Dashboard.scss';

export default function Dashboard() {
    const { user, system } = useContext(AuthContext);
    const api = useAPI();
    const history = useHistory();

    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState(false);
    const [steps, setSteps] = useState([]);
    const [completedStepCount, setCompletedStepCount] = useState(0);
    const [lastUpdatedAt, setLastUpdatedAt] = useState('');
    const [registrationMessage, setRegistrationMessage] = useState('');
    const [workflow, setWorkflow] = useState(null);
    const [payments, setPayments] = useState([]);

    const loadDashboard = () => {
        setLoading(true);
        setApiError(false);

        api.json('GET', '/systems/' + system.id + '/dashboard')
            .then((r) => r.json())
            .then((response) => {
                if (!response.data) {
                    return;
                }

                const apiSteps = response?.data?.registration_progress?.steps || [];
                let completeCount = 0;

                apiSteps.forEach(step => {
                    if (step.completed) {
                        completeCount++;
                    }
                });

                setSteps(apiSteps);
                setCompletedStepCount(completeCount);
                setLastUpdatedAt(response.data?.registration_progress?.last_updated_at || '');
                setRegistrationMessage(response.data?.registration_progress?.message || '');
                setWorkflow(response.data?.workflow || null);
                api.updateSystemData(response.data.system);
            })
            .catch(() => {
                setApiError(true);
            })
            .then(() => setLoading(false));
    };

    const loadPayments = () => {
        api.json('GET', '/systems/' + system.id + '/payments', { order_by: 'payment_date', order_dir: 'desc' })
            .then(r => r.json())
            .then(response => {
                if (!response.data) {
                    return;
                }
                setPayments(response.data || []);
            })
            .catch(() => {});
    };

    useEffect(() => {
        loadDashboard();
        loadPayments();
        // eslint-disable-next-line
    }, [system.id]);

    const res = system?.resource ?? {};
    const completedPayments = payments.filter(p =>
        p.status == null || COMPLETED_STATUSES.includes(p.status)
    );
    const totalEarned = completedPayments.reduce((sum, p) => sum + p.amount, 0);
    const totalRecs   = completedPayments.reduce((sum, p) => sum + (p.recs ?? 0), 0);
    const nextPayment = res.est_payment_date ? formatDate(res.est_payment_date) : '—';

    return (
        <Page busy={loading}>
            <div className="db-heading">
                <p>System ID: {system?.id}&nbsp;·&nbsp;{res.kw_dc} kW DC&nbsp;·&nbsp;{res.state}</p>
            </div>

            <DashboardStats
                totalEarned={totalEarned}
                totalRecs={totalRecs}
                completedPaymentsCount={completedPayments.length}
                nextPayment={nextPayment}
                res={res}
            />

            <RecentPayments
                payments={completedPayments}
                onViewAll={() => history.push('/payment-history')}
            />

            {!apiError && (
                <RegistrationProgress
                    steps={steps}
                    completedStepCount={completedStepCount}
                    registrationMessage={registrationMessage}
                    lastUpdatedAt={lastUpdatedAt}
                />
            )}

            <div className="db-two-col">
                <ContractDetails res={res} workflow={workflow} />
                <QuickActions />
            </div>
        </Page>
    );
}
